import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import SignIn from '@/pages/SignIn';
import DashboardLayout from '@/components/DashboardLayout';
import SimpleLayout from '@/components/SimpleLayout';
import EmailAttachmentsPage from '@/pages/EmailAttachmentsPage';
import './App.css';
import PdfViewerPage from './pages/PdfViewerPage';
import SpreadsheetViewerPage from './pages/SpreadsheetViewerPage';
import ImageViewerPage from './pages/ImageViewerPage';
import TextFileViewerPage from './pages/TextFileViewerPage';
import DocumentViewerPage from './pages/DocumentViewerPage';
import LogoutPage from './pages/LogoutPage';
import ProtectedRoute from './components/ProtectedRoute';
import EmailLimitSettings from "./pages/EmailProcess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Questions from "@/pages/questions";
import './assets/css/main.css'

declare module '@mui/material/styles' {
  interface Palette {
    icon: {
      view: string;
      approve: string;
      reject: string;
    };
  }
  interface PaletteOptions {
    icon: {
      view: string;
      approve: string;
      reject: string;
    };
  }
}

// Create Material UI theme with black, white, and gray color scheme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#000000', // Pure black for primary elements
      light: '#333333', // Dark gray for hover states
      dark: '#000000', // Pure black for pressed states
      contrastText: '#ffffff', // White text on primary
    },
    secondary: {
      main: '#666666', // Medium gray for secondary elements
      light: '#999999', // Light gray for hover states
      dark: '#444444', // Darker gray for pressed states
      contrastText: '#ffffff', // White text on secondary
    },
    icon: {
      view: '#2196F3',      // Blue for view
      approve: '#4CAF50',   // Green for approve
      reject: '#F44336',    // Red for reject
    },
    background: {
      default: '#ffff', // Light gray background (off-white)
      paper: '#ffffff', // Pure white for paper/card backgrounds
    },
    text: {
      primary: '#000000', // Black text for primary content
      secondary: '#666666', // Gray text for secondary content
    },
    divider: '#e0e0e0', // Light gray for dividers
    action: {
      hover: '#ffff', // Very light gray for hover effects
      selected: '#eeeeee', // Light gray for selected items
    },
  },
  typography: {
    fontFamily: 'Lato',
  },
  components: {
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)', // Default icon color
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          <Routes>
            {/* Public routes - all routes are now public */}
            <Route
              path="/login"
              element={<SignIn />}
            />
            <Route
              path="/questions/:id"
              element={<Questions />}
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                <DashboardLayout>
                  <EmailAttachmentsPage />
                </DashboardLayout>
                </ProtectedRoute>
              }
            />
        <Route
              path="/email-process-settings"
              element={
                <ProtectedRoute>
                <DashboardLayout>
                  <EmailLimitSettings />
                </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/logout"
              element={<LogoutPage />}
            />
 <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route 
              path="/pdf-viewer" 
              element={
                <ProtectedRoute>
                  <SimpleLayout>
                    <PdfViewerPage />
                  </SimpleLayout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/spreadsheet-viewer" 
              element={
                <ProtectedRoute>
                  <SpreadsheetViewerPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/image-viewer" 
              element={
                <ProtectedRoute>
                  <ImageViewerPage />
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="/text-viewer"
              element={
                <ProtectedRoute>
                  <TextFileViewerPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/document-viewer"
              element={
                <ProtectedRoute>
                  <SimpleLayout>
                    <DocumentViewerPage />
                  </SimpleLayout>
                </ProtectedRoute>
              }
            />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
